import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { Article } from '../types'
import { apiClient } from '../services/api'
import { useApiAuth } from '../hooks/useApiAuth'
import Header from '../components/Header'
import { getSiteName, getArticleTitle, isHtmlContent } from '../utils/articleUtils'

// Supported MIME types for attachment icons. Unmapped types show a generic paperclip.
function attachmentIcon(mimeType: string): string {
  if (mimeType === 'application/pdf') return '📄'
  if (mimeType.startsWith('image/')) return '🖼'
  if (mimeType.includes('word')) return '📝'
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📊'
  return '📎'
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function ArticleDetailPage() {
  const { ready: authReady } = useApiAuth()

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [article, setArticle] = useState<Article | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id || !authReady) return
    const abortController = new AbortController()
    apiClient
      .getArticle(id)
      .then((a) => {
        setArticle(a)
        // Mark as read. We update state optimistically rather than replacing it
        // with the server response because the update endpoint does not return
        // a contentUrl (which is only included on GET /articles/:id).
        if (!a.readAt) {
          apiClient
            .updateArticle(id, { readAt: new Date().toISOString() })
            .catch((err: unknown) => console.error('Failed to mark article as read:', err))
        }
        if (a.contentUrl) {
          setContentLoading(true)
          fetch(a.contentUrl, { signal: abortController.signal })
            .then((r) => r.text())
            .then(setContent)
            .catch((err: unknown) => {
              if (!(err instanceof Error) || err.name !== 'AbortError') {
                console.error('Failed to fetch article content:', err)
              }
              setContent(null)
            })
            .finally(() => setContentLoading(false))
        }
      })
      .catch((err: unknown) => {
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load article')
        }
      })
      .finally(() => setLoading(false))
    return () => abortController.abort()
  }, [id, authReady])

  async function toggleFavorite() {
    if (!article) return
    try {
      await apiClient.updateArticle(article.articleId, {
        isFavorite: !article.isFavorite,
      })
      setArticle({ ...article, isFavorite: !article.isFavorite })
    } catch (err: unknown) {
      console.error('Failed to toggle favorite:', err)
    }
  }

  if (loading) {
    return (
      <>
        <Header />
        <div className="loading-screen" style={{ minHeight: 'calc(100vh - 58px)' }}>
          <div className="spinner" />
        </div>
      </>
    )
  }

  if (error || !article) {
    return (
      <>
        <Header />
        <main className="page">
          <button className="back-link" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div className="empty-state">
            <h3>Article not found</h3>
            <p>{error}</p>
          </div>
        </main>
      </>
    )
  }

  const isEmail = article.source === 'email'
  const siteName = getSiteName(article)
  const title = getArticleTitle(article)
  const date = new Date(article.createdAt).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <>
      <Header />
      <main className="page">
        <div className="article-detail-header">
          <button className="back-link" onClick={() => navigate(-1)}>
            ← Back
          </button>

          <h1 className="article-detail-title">{title}</h1>

          <div className="article-detail-meta">
            <span>{siteName}</span>
            {!isEmail && article.author && (
              <>
                <span>·</span>
                <span>{article.author}</span>
              </>
            )}
            {isEmail && article.fromEmail && (
              <>
                <span>·</span>
                <span>{article.fromEmail}</span>
              </>
            )}
            <span>·</span>
            <span>{date}</span>
            {!isEmail && article.url && (
              <>
                <span>·</span>
                <a href={article.url} target="_blank" rel="noopener noreferrer">
                  Original ↗
                </a>
              </>
            )}
          </div>

          <div className="article-detail-actions">
            <button
              className={`btn-ghost${article.isFavorite ? ' active' : ''}`}
              onClick={toggleFavorite}
            >
              {article.isFavorite ? '★ Favorited' : '☆ Favorite'}
            </button>
          </div>

          {article.tags.length > 0 && (
            <div className="article-detail-tags">
              {article.tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <hr className="article-detail-divider" />
        </div>

        {contentLoading ? (
          <div className="loading-screen">
            <div className="spinner" />
          </div>
        ) : content ? (
          isHtmlContent(content) ? (
            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
            />
          ) : (
            <div className="article-content article-content--plaintext">{content}</div>
          )
        ) : (
          <div className="article-no-content">
            <p>No content was extracted for this article.</p>
            {!isEmail && article.url && (
              <a href={article.url} target="_blank" rel="noopener noreferrer">
                Read on original site ↗
              </a>
            )}
          </div>
        )}

        {isEmail && article.attachments && article.attachments.length > 0 && (
          <div className="article-attachments">
            <h4 className="article-attachments-heading">Attachments</h4>
            <ul className="article-attachments-list">
              {article.attachments.map((att) => (
                <li key={att.key} className="article-attachment-item">
                  {att.downloadUrl ? (
                    <a
                      href={att.downloadUrl}
                      download={att.filename}
                      className="article-attachment-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="article-attachment-icon">
                        {attachmentIcon(att.mimeType)}
                      </span>
                      <span className="article-attachment-name">{att.filename}</span>
                      <span className="article-attachment-size">{formatBytes(att.sizeBytes)}</span>
                    </a>
                  ) : (
                    <>
                      <span className="article-attachment-icon">
                        {attachmentIcon(att.mimeType)}
                      </span>
                      <span className="article-attachment-name">{att.filename}</span>
                      <span className="article-attachment-size">{formatBytes(att.sizeBytes)}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </>
  )
}
